import React from 'react';

/**
 * Emoji unicode → SVG twemoji.
 *
 * Le Chromium headless qui génère le PDF (repo `pdf`, @sparticuz/chromium-min)
 * n'embarque aucune police emoji couleur, contrairement à un navigateur de
 * bureau : les glyphes (ex. ☀️/🌙 des tableaux de tarification) restent
 * invisibles dans le PDF bien qu'ils s'affichent à l'écran. On les remplace
 * donc systématiquement par leur image, à l'écran comme en PDF, pour un rendu
 * identique dans les deux — `twemoji` est chargé globalement depuis
 * `public/index.html`.
 */
const TWEMOJI_BASE = 'https://cdn.jsdelivr.net/gh/jdecked/twemoji@17.0.3/assets/';

function emojiSvgUrl(emoji) {
  const tw = typeof window !== 'undefined' ? window.twemoji : undefined;
  if (!tw) return null;
  const tag = tw.parse(emoji, { folder: 'svg', ext: '.svg', base: TWEMOJI_BASE });
  const match = /src="([^"]+)"/.exec(tag);
  return match ? match[1] : null;
}

/**
 * Découpe un texte en alternant fragments de texte et emoji. Le texte hors
 * emoji reste porté par des noeuds React (donc échappé normalement) : seuls
 * les caractères emoji, isolés par la regex interne de twemoji via
 * `replace`, sont transformés en `<img>`. On ne passe jamais le texte libre
 * par innerHTML/dangerouslySetInnerHTML — ces cellules sont éditables par
 * l'utilisateur.
 */
export function renderTextWithEmoji(text, keyPrefix = '') {
  if (typeof text !== 'string' || !text) return text;
  const tw = typeof window !== 'undefined' ? window.twemoji : undefined;
  if (!tw || typeof tw.test !== 'function' || !tw.test(text)) return text;

  const nodes = [];
  let cursor = 0;
  let index = 0;
  tw.replace(text, (match, _group, offset) => {
    if (offset > cursor) {
      nodes.push(<React.Fragment key={`${keyPrefix}t${index}`}>{text.slice(cursor, offset)}</React.Fragment>);
      index += 1;
    }
    const src = emojiSvgUrl(match);
    nodes.push(
      src ? (
        <img
          key={`${keyPrefix}e${index}`}
          src={src}
          alt={match}
          draggable="false"
          style={{
            height: '1em', width: '1em', verticalAlign: '-0.15em', display: 'inline-block',
          }}
        />
      ) : <React.Fragment key={`${keyPrefix}e${index}`}>{match}</React.Fragment>,
    );
    index += 1;
    cursor = offset + match.length;
    return match;
  });
  if (cursor < text.length) {
    nodes.push(<React.Fragment key={`${keyPrefix}t${index}`}>{text.slice(cursor)}</React.Fragment>);
  }
  return nodes;
}

/**
 * Résolution des styles d'un document : libellé de couleur → CSS, clé de police
 * → nom de famille. Exporté pour `draftRawToHtml`, qui doit produire exactement
 * les mêmes couleurs et polices que ce rendu.
 */
export function buildDraftStyleContext(styleContext = {}) {
  const colorMap = new Map();
  const familyMap = new Map();

  (styleContext.colors || []).forEach((c) => {
    if (c?.label && c?.style) colorMap.set(c.label, c.style);
  });
  (styleContext.fontFamilyList || []).forEach((f) => {
    if (f?.key && f?.familyName) familyMap.set(`FONT_FAMILY_${String(f.key).replace(/\s+/g, '_')}`, f.familyName);
  });

  return { colorMap, familyMap };
}

/**
 * Applique les styles inline Draft.js à un texte.
 * Gère : BOLD, ITALIC, UNDERLINE, FONT_SIZE_xx, FONT_FAMILY_xxx, color_xxx
 */
function applyInlineStyles(text, ranges, styleContext = {}) {
  if (!ranges?.length || !text) return renderTextWithEmoji(text, 'plain-');
  const { colorMap, familyMap } = buildDraftStyleContext(styleContext);

  // Construire un tableau de segments avec les styles cumulés
  const chars = Array.from(text).map((char, i) => ({
    char,
    styles: ranges
      .filter((r) => i >= r.offset && i < r.offset + r.length)
      .map((r) => r.style),
  }));

  // Regrouper les caractères consécutifs avec les mêmes styles
  const segments = [];
  for (const c of chars) {
    const key = c.styles.slice().sort().join('|');
    const last = segments[segments.length - 1];
    if (last && last.key === key) {
      last.text += c.char;
    } else {
      segments.push({ key, styles: c.styles, text: c.char });
    }
  }

  return segments.map((seg, i) => {
    if (!seg.styles.length) return <React.Fragment key={i}>{renderTextWithEmoji(seg.text, `seg${i}-`)}</React.Fragment>;

    const fontSizeStyle = seg.styles.find((s) => s.startsWith('FONT_SIZE_'));
    const fontFamilyStyle = seg.styles.find((s) => s.startsWith('FONT_FAMILY_'));
    const colorStyle = seg.styles.find((s) => s.startsWith('color_') || s === 'white' || s === 'black');
    const fontSize = fontSizeStyle ? parseInt(fontSizeStyle.replace('FONT_SIZE_', ''), 10) : null;
    const family = fontFamilyStyle
      ? (familyMap.get(fontFamilyStyle) || fontFamilyStyle.replace('FONT_FAMILY_', '').replace(/_/g, ' '))
      : null;
    const color = colorStyle
      ? (colorMap.get(colorStyle) || (colorStyle === 'white' ? '#fff' : colorStyle === 'black' ? '#000' : null))
      : null;

    let content = renderTextWithEmoji(seg.text, `seg${i}-`);
    if (seg.styles.includes('BOLD')) content = <strong>{content}</strong>;
    if (seg.styles.includes('ITALIC')) content = <em>{content}</em>;
    if (seg.styles.includes('UNDERLINE')) content = <u>{content}</u>;
    const inlineStyle = {
      ...(fontSize ? { fontSize } : {}),
      ...(family ? { fontFamily: `${family}, sans-serif` } : {}),
      ...(color ? { color } : {}),
    };
    if (Object.keys(inlineStyle).length) content = <span style={inlineStyle}>{content}</span>;

    return <React.Fragment key={i}>{content}</React.Fragment>;
  });
}

/**
 * Convertit une valeur cellule qui peut être :
 *  - un string Draft.js JSON  → rendu en React elements
 *  - un string plain text     → rendu tel quel
 *  - autre                    → rendu tel quel
 */
export function renderDraftContent(value, options = {}) {
  if (!value || typeof value !== 'string') return value ?? '';

  // Tenter de parser comme Draft.js content state
  let content;
  try {
    content = JSON.parse(value);
  } catch {
    return renderTextWithEmoji(value, 'plain-');
  }

  if (!content?.blocks?.length) return renderTextWithEmoji(value, 'plain-');

  const elements = [];
  let listItems = [];
  let currentListType = null;
  const paragraphStyle = {
    margin: '2px 0',
    ...(options.defaultStyle || {}),
  };

  const flushList = (key) => {
    if (!listItems.length) return;
    const ListTag = currentListType === 'ordered-list-item' ? 'ol' : 'ul';
    elements.push(
      <ListTag key={`list-${key}`} style={{ margin: '2px 0 2px 16px', paddingLeft: 8 }}>
        {listItems}
      </ListTag>,
    );
    listItems = [];
    currentListType = null;
  };

  content.blocks.forEach((block, i) => {
    const styledText = applyInlineStyles(block.text, block.inlineStyleRanges, options.styleContext);
    const isList = block.type === 'unordered-list-item' || block.type === 'ordered-list-item';

    if (isList) {
      if (currentListType && currentListType !== block.type) flushList(i);
      currentListType = block.type;
      listItems.push(<li key={i}>{styledText}</li>);
    } else {
      flushList(i);
      if (block.text) {
        elements.push(
          <p key={i} style={paragraphStyle}>
            {styledText}
          </p>,
        );
      }
    }
  });
  flushList('end');

  return elements.length ? <>{elements}</> : '';
}
